/**
 * SOLOMON — USER SETTINGS PANEL
 * Collapsible glass accordion sections for voice and display settings.
 */

import { useState, useEffect, useCallback, useRef, useId } from 'react';
import { useAuth } from '../../context/AuthContext';
import { pushLayer, popLayer, isTopLayer } from '../../layers';
import IntegrationsPanel from './IntegrationsPanel';
import './settings.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function ChevronIcon({ expanded }) {
  return (
    <svg
      className={`accordion-chevron ${expanded ? 'accordion-chevron--open' : ''}`}
      width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
  );
}

function DisplayIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function IntegrationsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export default function SettingsPanel({
  isOpen,
  onClose,
  // Deep-link target for the accordion: the capability map's "needs attention"
  // rows carry `action: { type: 'settings', target: 'integrations' }`, and a
  // row that opened Settings collapsed would land the operator one click short
  // of the thing it just told them was broken.
  initialSection = null,
  voiceEnabled,
  selectedVoiceId,
  onToggleVoice,
  onSelectVoice,
  fetchVoices,
  theme,
  onThemeToggle,
  // Integrations
  gmailConnected = false,
  onConnectGmail,
  onDisconnectGmail,
}) {
  const { authFetch } = useAuth();
  const [voices, setVoices]             = useState([]);
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const [openSection, setOpenSection]   = useState(initialSection); // accordion: null | 'voice' | 'display' | 'integrations'
  const [voiceDropdownOpen, setVoiceDropdownOpen] = useState(false);
  const panelRef = useRef(null);
  const voiceDropdownRef = useRef(null);

  // The toggles are icon-only — no text node, no name. `aria-pressed` alone
  // announces a state with nothing to attach it to, so each one borrows the
  // adjacent `.settings-label` by id.
  const voiceLabelId = useId();
  const themeLabelId = useId();

  // Load voices when panel opens
  useEffect(() => {
    if (!isOpen || voicesLoaded) return;
    (async () => {
      try {
        const list = await fetchVoices();
        if (Array.isArray(list)) setVoices(list);
        setVoicesLoaded(true);
      } catch {}
    })();
  }, [isOpen, voicesLoaded, fetchVoices]);

  // Close on escape — gated on the layer stack so only the TOPMOST open
  // overlay consumes it. This was the fourth unguarded global ESC handler in
  // the app (the other three were migrated in the layer-stack task); left
  // ungated, Settings would still close on any ESC even with a dialog opened
  // over it. Binds `document` rather than `window` as it always has.
  const layerId = useId();
  useEffect(() => {
    if (!isOpen) return undefined;
    pushLayer(layerId);
    const handleKey = (e) => { if (e.key === 'Escape' && isTopLayer(layerId)) onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => { popLayer(layerId); document.removeEventListener('keydown', handleKey); };
  }, [isOpen, onClose, layerId]);

  // Close voice dropdown on outside click
  useEffect(() => {
    if (!voiceDropdownOpen) return;
    const handleClick = (e) => {
      if (voiceDropdownRef.current && !voiceDropdownRef.current.contains(e.target)) {
        setVoiceDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [voiceDropdownOpen]);

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setOpenSection(null);
      setVoiceDropdownOpen(false);
    }
  }, [isOpen]);

  // Deep-link. Runs on every open (not just mount) because the panel is
  // mounted-and-unmounted by `isOpen` here but the caller may re-open it at a
  // different section without the initial state ever being re-read.
  useEffect(() => {
    // Assign unconditionally, including null. Setting only when `initialSection`
    // is truthy meant a deep-link (Google attention row → Integrations) left the
    // accordion expanded for every later generic "Open settings" click, because
    // nothing ever reset it.
    if (isOpen) setOpenSection(initialSection);
  }, [isOpen, initialSection]);

  const toggleSection = useCallback((section) => {
    setOpenSection(prev => prev === section ? null : section);
    setVoiceDropdownOpen(false);
  }, []);

  const handleVoiceSelect = useCallback((voiceId) => {
    onSelectVoice(voiceId);
    setVoiceDropdownOpen(false);
  }, [onSelectVoice]);

  if (!isOpen) return null;

  const selectedVoice = voices.find(v => v.voice_id === selectedVoiceId);
  const selectedVoiceName = selectedVoice?.name || 'Default';

  // Preview strings for collapsed state
  const voicePreview = voiceEnabled ? selectedVoiceName : 'Off';
  const displayPreview = theme === 'dark' ? 'Dark' : 'Light';
  const connectedCount = [gmailConnected].filter(Boolean).length;
  const integrationsPreview = connectedCount > 0 ? `${connectedCount} connected` : 'None';

  return (
    <>
      <div className="settings-backdrop" onClick={onClose} />
      <div className="settings-panel" ref={panelRef}>
        {/* Header */}
        <div className="settings-header">
          <h2 className="settings-title">Settings</h2>
          <button className="settings-close" onClick={onClose} aria-label="Close settings">
            <CloseIcon />
          </button>
        </div>

        <div className="settings-body">

          {/* ── Voice Section ── */}
          <div className={`accordion-section ${openSection === 'voice' ? 'accordion-section--open' : ''}`}>
            <button
              className="accordion-header"
              onClick={() => toggleSection('voice')}
              aria-expanded={openSection === 'voice'}
            >
              <span className="accordion-header-left">
                <span className="accordion-icon"><SpeakerIcon /></span>
                <span className="accordion-name">Voice</span>
              </span>
              <span className="accordion-header-right">
                <span className="accordion-preview">{voicePreview}</span>
                <ChevronIcon expanded={openSection === 'voice'} />
              </span>
            </button>

            <div className="accordion-content">
              <div className="accordion-content-inner">
                {/* Voice output toggle */}
                <div className="settings-row">
                  <div className="settings-row-info">
                    <span className="settings-label" id={voiceLabelId}>Voice Output</span>
                    <span className="settings-hint">Solomon speaks responses aloud</span>
                  </div>
                  <button
                    className={`settings-toggle ${voiceEnabled ? 'settings-toggle--on' : ''}`}
                    onClick={onToggleVoice}
                    aria-pressed={voiceEnabled}
                    aria-labelledby={voiceLabelId}
                  >
                    <span className="settings-toggle-thumb" />
                  </button>
                </div>

                {/* Voice selection dropdown */}
                {voiceEnabled && (
                  <div className="settings-row settings-row--vertical" ref={voiceDropdownRef}>
                    <span className="settings-label">Voice Selection</span>
                    <div className="voice-select-wrapper">
                      <button
                        className="voice-select-trigger"
                        onClick={() => setVoiceDropdownOpen(prev => !prev)}
                      >
                        <span className="voice-select-value">{selectedVoiceName}</span>
                        <svg
                          className={`voice-select-arrow ${voiceDropdownOpen ? 'voice-select-arrow--open' : ''}`}
                          width="12" height="12" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>

                      {voiceDropdownOpen && (
                        <div className="voice-select-dropdown">
                          {voices.length === 0 && (
                            <span className="voice-select-loading">Loading voices...</span>
                          )}
                          {voices.map(v => (
                            <button
                              key={v.voice_id}
                              className={`voice-select-option ${selectedVoiceId === v.voice_id ? 'voice-select-option--active' : ''}`}
                              onClick={() => handleVoiceSelect(v.voice_id)}
                            >
                              <span className="voice-select-option-name">{v.name}</span>
                              {v.labels && (
                                <span className="voice-select-option-desc">
                                  {[v.labels.gender, v.labels.accent, v.labels.use_case].filter(Boolean).join(' · ')}
                                </span>
                              )}
                              {selectedVoiceId === v.voice_id && (
                                <span className="voice-select-check">&#10003;</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Display Section ── */}
          <div className={`accordion-section ${openSection === 'display' ? 'accordion-section--open' : ''}`}>
            <button
              className="accordion-header"
              onClick={() => toggleSection('display')}
              aria-expanded={openSection === 'display'}
            >
              <span className="accordion-header-left">
                <span className="accordion-icon"><DisplayIcon /></span>
                <span className="accordion-name">Display</span>
              </span>
              <span className="accordion-header-right">
                <span className="accordion-preview">{displayPreview}</span>
                <ChevronIcon expanded={openSection === 'display'} />
              </span>
            </button>

            <div className="accordion-content">
              <div className="accordion-content-inner">
                <div className="settings-row">
                  <div className="settings-row-info">
                    <span className="settings-label" id={themeLabelId}>Theme</span>
                    <span className="settings-hint">{theme === 'dark' ? 'Dark mode' : 'Light mode'}</span>
                  </div>
                  <button
                    className={`settings-toggle ${theme === 'dark' ? 'settings-toggle--on' : ''}`}
                    onClick={onThemeToggle}
                    aria-pressed={theme === 'dark'}
                    aria-labelledby={themeLabelId}
                  >
                    <span className="settings-toggle-thumb" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Integrations Section ── */}
          <div className={`accordion-section ${openSection === 'integrations' ? 'accordion-section--open' : ''}`}>
            <button
              className="accordion-header"
              onClick={() => toggleSection('integrations')}
              aria-expanded={openSection === 'integrations'}
            >
              <span className="accordion-header-left">
                <span className="accordion-icon"><IntegrationsIcon /></span>
                <span className="accordion-name">Integrations</span>
              </span>
              <span className="accordion-header-right">
                <span className="accordion-preview">{integrationsPreview}</span>
                <ChevronIcon expanded={openSection === 'integrations'} />
              </span>
            </button>

            <div className="accordion-content">
              <div className="accordion-content-inner">
                <IntegrationsPanel
                  gmailConnected={gmailConnected}
                  onConnectGmail={onConnectGmail}
                  onDisconnectGmail={onDisconnectGmail}
                />
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="settings-footer">
          <span className="settings-footer-text">Solomon v1.0 — Phase 2</span>
        </div>
      </div>
    </>
  );
}
